import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { useBulkCheckUpdateErrors, useCheckTypes, useWorkflowStatus } from '../hooks/useMasterData';
import SearchableDropdown from '../components/SearchableDropdown';
import RowValidationErrorsCell from '../components/RowValidationErrorsCell';
import Tooltip from '../components/Tooltip';
import UserTimestamp from '../components/UserTimestamp';
import UploadProgressCard from '../components/UploadProgressCard';
import { MdOutlineVisibility, MdOutlineEdit } from 'react-icons/md';
import { Trash2, ArrowUpCircle } from 'lucide-react';
import { resolveUploadJobName } from '../utils/uploadJobName';
import * as XLSX from 'xlsx';
import './CheckUpload.css';

const formatDateTime = (dt) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const getRowStatusStyle = (rowStatus) => {
  const map = {
    VALID: { background: '#d1fae5', color: '#059669' },
    INVALID: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
    PROMOTED: { background: '#dbeafe', color: '#2563eb' },
  };
  return map[rowStatus] || { background: '#f3f4f6', color: '#6b7280' };
};

const getJobStatusStyle = (status) => {
  const map = {
    READY_TO_PROMOTE: { background: '#d1fae5', color: '#059669' },
    CORRECTION_REQUIRED: { background: '#fee2e2', color: '#dc2626' },
    COMPLETED: { background: '#d1fae5', color: '#059669' },
    FAILED: { background: '#fee2e2', color: '#dc2626' },
    PENDING: { background: '#fef3c7', color: '#d97706' },
    PROMOTED: { background: '#dbeafe', color: '#2563eb' },
  };
  return map[status] || { background: '#f3f4f6', color: '#6b7280' };
};

const formatJobStatus = (status) => {
  if (!status) return 'PENDING';
  return status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const formatRowNumber = (n) => {
  if (n == null) return '-';
  const num = Number(n);
  return isNaN(num) ? String(n) : num >= 1000 ? String(num) : String(num).padStart(3, '0');
};

/** First non-empty property from row (handles alternate API key casing). */
const firstRowString = (row, keys) => {
  if (!row) return null;
  for (const k of keys) {
    const v = row[k];
    if (v != null && String(v).trim() !== '') return v;
  }
  return null;
};

const resolveUserIdFromRow = (row, field, idField, users) => {
  const idVal = idField ? row?.[idField] : null;
  if (idVal != null && String(idVal).trim() !== '') return String(idVal).trim();
  const val = row?.[field];
  if (val == null || String(val).trim() === '') return '';
  const s = String(val).trim();
  const byId = (users || []).find((u) => (u.userId || u.id) === s);
  if (byId) return byId.userId || byId.id;
  const byEmail = (users || []).find((u) => u.email && u.email.toLowerCase() === s.toLowerCase());
  if (byEmail) return byEmail.userId || byEmail.id;
  const byName = (users || []).find((u) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return name && name.toLowerCase() === s.toLowerCase();
  });
  if (byName) return byName.userId || byName.id;
  return s;
};

const TruncatedText = ({ text, maxLength = 25 }) => {
  if (!text) return <span>-</span>;
  const should = text.length > maxLength;
  const display = should ? text.substring(0, maxLength) + '...' : text;
  return should ? (
    <Tooltip text={text} position="bottom">
      <span className="truncated-text">{display}</span>
    </Tooltip>
  ) : <span>{display}</span>;
};

const TruncatedName = ({ text, maxLength = 12 }) => {
  if (!text) return <span>-</span>;
  const should = text.length > maxLength;
  const display = should ? text.substring(0, maxLength) + '...' : text;
  return should ? (
    <Tooltip text={text} position="bottom">
      <span className="truncated-text">{display}</span>
    </Tooltip>
  ) : <span>{display}</span>;
};

const ErrorsBlock = ({ validationErrors }) => {
  const hasErrors = validationErrors && validationErrors.length > 0;
  if (!hasErrors) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '8px 12px' }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: '#dc2626', textTransform: 'uppercase', marginBottom: '6px' }}>Validation Errors</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px' }}>
        {validationErrors.map((e, i) => (
          <div key={i} style={{ fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
            <span style={{ color: '#dc2626', marginTop: '1px', flexShrink: 0 }}>•</span>
            <span>{e}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value, wide }) => (
  <div style={{ gridColumn: wide ? '1 / -1' : 'auto', marginBottom: '12px' }}>
    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{label}</div>
    <div style={{ fontSize: '14px', color: '#111827', wordBreak: 'break-word' }}>{value || <span style={{ color: '#9ca3af' }}>—</span>}</div>
  </div>
);

const EMPTY_EDIT = {
  checkNumber: '',
  practiceCode: '',
  locationCode: '',
  checkType: '',
  payer: '',
  exchange: '',
  workflowStatus: '',
  bankTrnRef: '',
  assignee: '',
  reportee: '',
  legacyNotes: '',
};

const CheckUpdate = () => {
  const { showError, showSuccess } = useNotification();
  const { users, getUserById, getUserName } = useUsers();
  const { data: errorTypes, loading: errorTypesLoading } = useBulkCheckUpdateErrors(true);
  const { data: checkTypes, loading: checkTypesLoading } = useCheckTypes(true);
  const { data: workflowStatuses, loading: workflowStatusesLoading } = useWorkflowStatus(true);

  const [dashboardJobs, setDashboardJobs] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobSearch, setJobSearch] = useState('');
  const [dashboardPage, setDashboardPage] = useState(0);
  const [dashboardTotalElements, setDashboardTotalElements] = useState(0);
  const [dashboardTotalPages, setDashboardTotalPages] = useState(0);
  const [dashboardHasMore, setDashboardHasMore] = useState(false);
  const [dashboardPageInputValue, setDashboardPageInputValue] = useState('1');

  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jobName, setJobName] = useState('');
  const [practiceCode, setPracticeCode] = useState('');
  const [locationCode, setLocationCode] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({
    active: false,
    percent: 0,
    stageIndex: 0,
    stageLabel: 'Preparing',
    helperText: '',
    indeterminate: false,
  });

  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState('');

  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [filterErrors, setFilterErrors] = useState([]);
  const [rowSearch, setRowSearch] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  const [viewingRow, setViewingRow] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState(EMPTY_EDIT);
  const [savingEdit, setSavingEdit] = useState(false);

  const [revalidating, setRevalidating] = useState(false);
  const [promotingAll, setPromotingAll] = useState(false);
  const [promotingRowId, setPromotingRowId] = useState(null);
  const [promoteModalRow, setPromoteModalRow] = useState(null);
  const [promoteFormData, setPromoteFormData] = useState(null);
  const [promotingFromModal, setPromotingFromModal] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [showDeleteRowConfirm, setShowDeleteRowConfirm] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);

  /** Practice/locations for Edit and Promote row modals (separate from upload modal) */
  const [stagedRowPracticeId, setStagedRowPracticeId] = useState('');
  const [stagedRowLocations, setStagedRowLocations] = useState([]);
  const [loadingStagedRowLocations, setLoadingStagedRowLocations] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      setLoadingPractices(true);
      try {
        const data = await api.getAllPractices();
        setPractices((Array.isArray(data) ? data : []).filter(p => p.isActive !== false));
      } catch (e) { console.error(e); } finally { setLoadingPractices(false); }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (!selectedPracticeId) { setLocations([]); setLocationCode(''); return; }
    const fetch = async () => {
      setLoadingLocations(true);
      try {
        const data = await api.getPracticeLocations(selectedPracticeId);
        setLocations((Array.isArray(data) ? data : []).filter(l => l.isActive !== false));
      } catch (e) { console.error(e); setLocations([]); } finally { setLoadingLocations(false); }
    };
    fetch();
  }, [selectedPracticeId]);

  useEffect(() => {
    if (!stagedRowPracticeId) {
      setStagedRowLocations([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingStagedRowLocations(true);
      try {
        const data = await api.getPracticeLocations(stagedRowPracticeId);
        if (!cancelled) {
          setStagedRowLocations((Array.isArray(data) ? data : []).filter((l) => l.isActive !== false));
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setStagedRowLocations([]);
      } finally {
        if (!cancelled) setLoadingStagedRowLocations(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stagedRowPracticeId]);

  const fetchDashboard = useCallback(async (page = 0, searchText = '') => {
    setLoadingDashboard(true);
    try {
      const search = String(searchText || '').trim();
      const response = await api.getBulkCheckUpdateJobs({
        page,
        size: 50,
        ...(search ? { jobName: search } : {}),
      });
      setDashboardJobs(response?.items || []);
      const totalPagesCount = response?.totalPages || 0;
      setDashboardTotalElements(response?.totalElements || 0);
      setDashboardTotalPages(totalPagesCount);
      setDashboardHasMore(page < totalPagesCount - 1);
      setDashboardPage(page);
      setDashboardPageInputValue((page + 1).toString());
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to load jobs.';
      showError(msg);
    } finally { setLoadingDashboard(false); }
  }, [showError]);

  const fetchJobRows = useCallback(async (jobId, page = 0, errorSearches = []) => {
    setLoadingRows(true);
    try {
      const [jobDetails, response] = await Promise.all([
        api.getBulkCheckUpdateJob(jobId),
        api.getBulkCheckUpdateJobRows(jobId, { page, size: 50, errorSearches }),
      ]);
      setSelectedJob(jobDetails);
      setRows(response.items || []);
      setTotalElements(response.totalElements || 0);
      setTotalPages(response.totalPages || 0);
      setCurrentPage(page);
    } catch (err) {
      const msg = err.data?.message || err.message || 'Failed to load rows.';
      showError(msg);
    } finally { setLoadingRows(false); }
  }, [showError]);

  useEffect(() => { fetchDashboard(dashboardPage, jobSearch); }, [fetchDashboard, dashboardPage, jobSearch]);

  useEffect(() => {
    setDashboardPage(0);
    setDashboardPageInputValue('1');
  }, [jobSearch]);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };
  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: '#6366f1' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const isValidFileType = (f) =>
    ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      .includes(f.type) || f.name.endsWith('.csv') || f.name.endsWith('.xlsx');

  const processSelectedFile = async (f) => {
    if (!isValidFileType(f)) { showError('Please select a CSV or XLSX file'); setFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { showError('Maximum allowed file size is 10 MB.'); setFile(null); return; }
    setFile(f);
  };

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) await processSelectedFile(dropped);
  };
  const handleFileInput = async (e) => {
    const selected = e.target.files?.[0];
    if (selected) await processSelectedFile(selected);
    e.target.value = '';
  };

  const resetUploadModal = () => {
    setFile(null); setJobName(''); setPracticeCode(''); setLocationCode(''); setSelectedPracticeId('');
    setUploadProgress((prev) => ({ ...prev, active: false }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!practiceCode) { showError('Practice is required'); return; }
    if (!locationCode) { showError('Location is required'); return; }
    setUploading(true);
    setUploadProgress({
      active: true,
      percent: 10,
      stageIndex: 0,
      stageLabel: 'Preparing file',
      helperText: 'Preparing request and metadata...',
      indeterminate: false,
    });
    try {
      setUploadProgress({
        active: true,
        percent: 30,
        stageIndex: 1,
        stageLabel: 'Validating input',
        helperText: 'Checking selected practice and location...',
        indeterminate: false,
      });
      const resolvedJobName = resolveUploadJobName(jobName, { practiceCode, locationCode });
      const response = await api.createBulkCheckUpdateJob(file, practiceCode, locationCode, resolvedJobName, {
        onUploadProgress: (progressEvent) => {
          const uploadPercent = progressEvent?.progress;
          const mapped = uploadPercent == null
            ? 62
            : Math.min(80, Math.max(45, 45 + Math.round((uploadPercent / 100) * 35)));
          setUploadProgress({
            active: true,
            percent: mapped,
            stageIndex: 2,
            stageLabel: 'Uploading file',
            helperText: uploadPercent == null
              ? 'Uploading staged updates...'
              : `Uploading... ${uploadPercent}% complete`,
            indeterminate: uploadPercent == null,
          });
        },
      });
      setUploadProgress({
        active: true,
        percent: 92,
        stageIndex: 3,
        stageLabel: 'Finalizing validation',
        helperText: 'Completing server-side checks...',
        indeterminate: true,
      });
      showSuccess('File uploaded successfully!');
      setShowUploadModal(false); resetUploadModal();
      await fetchDashboard(dashboardPage, jobSearch);
      if (response.job) await fetchJobRows(response.job.jobId, 0);
    } catch (err) {
      showError(err.data?.message || err.message || 'Failed to upload file.');
      setUploadProgress({
        active: true,
        percent: 100,
        stageIndex: 3,
        stageLabel: 'Upload failed',
        helperText: err.data?.message || err.message || 'Failed to upload file.',
        indeterminate: false,
      });
    } finally { setUploading(false); }
  };

  const handleFilterChange = (val) => {
    const newErrors = val ? [val] : [];
    setFilterErrors(newErrors);
    if (selectedJob) fetchJobRows(selectedJob.jobId, 0, newErrors);
  };

  const resolvePracticeIdFromCode = useCallback((code) => {
    if (!code) return '';
    const c = String(code).trim().toLowerCase();
    const p = practices.find((x) => String(x.code ?? '').trim().toLowerCase() === c);
    return p ? (p.practiceId || p.id) : '';
  }, [practices]);

  const assigneePickerOptions = useMemo(() => [
    { value: '', label: 'Select assignee' },
    ...users.map((u) => ({
      value: u.userId || u.id,
      label: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : (u.email || u.userId || u.id),
    })),
  ], [users]);
  const reporteePickerOptions = useMemo(() => [
    { value: '', label: 'Select reportee' },
    ...users.map((u) => ({
      value: u.userId || u.id,
      label: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : (u.email || u.userId || u.id),
    })),
  ], [users]);

  const handleEditRow = (row) => {
    setEditingRow(row);
    setStagedRowPracticeId(resolvePracticeIdFromCode(row.practiceCode));
    setEditFormData({
      checkNumber: row.checkNumber || '',
      practiceCode: row.practiceCode || '',
      locationCode: row.locationCode || '',
      checkType: row.checkType || '',
      payer: row.payer || '',
      exchange: row.exchange || '',
      workflowStatus: row.workflowStatus || '',
      bankTrnRef: row.bankTrnRef || '',
      assignee: resolveUserIdFromRow(row, 'assignee', 'assigneeId', users),
      reportee: resolveUserIdFromRow(row, 'reportee', 'reporterId', users),
      legacyNotes: row.legacyNotes || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRow || !selectedJob) return;
    if (!editFormData.practiceCode?.trim()) {
      showError('Practice is required');
      return;
    }
    if (!editFormData.locationCode?.trim()) {
      showError('Location is required');
      return;
    }
    if (!editFormData.assignee?.trim()) {
      showError('Assignee is required');
      return;
    }
    if (!editFormData.reportee?.trim()) {
      showError('Reportee is required');
      return;
    }
    setSavingEdit(true);
    try {
      await api.updateBulkCheckUpdateRow(selectedJob.jobId, editingRow.stagingUpdateId, editFormData);
      const updatedJob = await api.revalidateBulkCheckUpdateJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      setEditingRow(null); setEditFormData(EMPTY_EDIT); setStagedRowPracticeId('');
      showSuccess('Row updated and re-validated!');
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to update row.');
    } finally { setSavingEdit(false); }
  };

  const openPromoteModal = (row) => {
    setPromoteModalRow(row);
    setStagedRowPracticeId(resolvePracticeIdFromCode(row.practiceCode));
    setPromoteFormData({
      checkNumber: row.checkNumber || '',
      practiceCode: row.practiceCode || '',
      locationCode: row.locationCode || '',
      checkType: row.checkType || '',
      payer: row.payer || '',
      exchange: row.exchange || '',
      workflowStatus: row.workflowStatus || '',
      bankTrnRef: row.bankTrnRef || '',
      assignee: resolveUserIdFromRow(row, 'assignee', 'assigneeId', users),
      reportee: resolveUserIdFromRow(row, 'reportee', 'reporterId', users),
      legacyNotes: row.legacyNotes || '',
    });
  };

  const closePromoteModal = () => {
    if (promotingFromModal) return;
    setPromoteModalRow(null);
    setPromoteFormData(null);
    setStagedRowPracticeId('');
  };

  const handleConfirmPromoteFromModal = async () => {
    if (!selectedJob || !promoteModalRow || !promoteFormData) return;
    if (!promoteFormData.practiceCode?.trim()) {
      showError('Practice is required');
      return;
    }
    if (!promoteFormData.locationCode?.trim()) {
      showError('Location is required');
      return;
    }
    if (!promoteFormData.assignee?.trim()) {
      showError('Assignee is required');
      return;
    }
    if (!promoteFormData.reportee?.trim()) {
      showError('Reportee is required');
      return;
    }
    setPromotingFromModal(true);
    setPromotingRowId(promoteModalRow.stagingUpdateId);
    try {
      await api.updateBulkCheckUpdateRow(selectedJob.jobId, promoteModalRow.stagingUpdateId, promoteFormData);
      const updatedJob = await api.revalidateBulkCheckUpdateJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      await api.promoteBulkCheckUpdateRow(selectedJob.jobId, promoteModalRow.stagingUpdateId);
      showSuccess('Row promoted successfully!');
      setPromoteModalRow(null);
      setPromoteFormData(null);
      setStagedRowPracticeId('');
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err?.data?.message || err?.message || String(err);
      showError(msg);
    } finally {
      setPromotingFromModal(false);
      setPromotingRowId(null);
    }
  };

  const handleRevalidate = async () => {
    if (!selectedJob) return;
    setRevalidating(true);
    try {
      const updatedJob = await api.revalidateBulkCheckUpdateJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      showSuccess('Job re-validated successfully!');
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to re-validate job.';
      showError(msg);
    } finally { setRevalidating(false); }
  };

  const handlePromoteAll = async () => {
    if (!selectedJob) return;
    setPromotingAll(true);
    try {
      const updatedJob = await api.promoteBulkCheckUpdateJob(selectedJob.jobId);
      setSelectedJob(updatedJob);
      showSuccess('Valid rows promoted successfully!');
      await fetchDashboard(dashboardPage, jobSearch);
      await fetchJobRows(selectedJob.jobId, currentPage, filterErrors);
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to promote rows.';
      showError(msg);
    } finally { setPromotingAll(false); }
  };

  const handleExportReport = async () => {
    if (!selectedJob) return;
    setExporting(true);
    try {
      const blob = await api.exportBulkCheckUpdateReport(selectedJob.jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedJob.jobName || 'CheckUpdate'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Error report exported successfully!');
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to export error report.';
      showError(msg);
    } finally { setExporting(false); }
  };

  const handleDeleteClick = (job, e) => {
    e.stopPropagation();
    setJobToDelete(job);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return;
    setDeleting(true);
    try {
      await api.deleteBulkCheckUpdateJob(jobToDelete.jobId);
      showSuccess(`Job "${jobToDelete.jobName || 'Unnamed Job'}" deleted successfully!`);
      if (selectedJob?.jobId === jobToDelete.jobId) { setSelectedJob(null); setRows([]); }
      await fetchDashboard(dashboardPage, jobSearch);
      setShowDeleteConfirm(false); setJobToDelete(null);
    } catch (err) {
      showError(err.data?.message || err.message || 'Failed to delete job.');
    } finally { setDeleting(false); }
  };

  const handleDeleteRowClick = (row, e) => {
    e?.stopPropagation?.();
    setRowToDelete(row);
    setShowDeleteRowConfirm(true);
  };

  const handleDeleteRowConfirm = async () => {
    if (!selectedJob || !rowToDelete) return;
    const sid = rowToDelete.stagingUpdateId;
    setDeletingRowId(sid);
    try {
      await api.deleteBulkCheckUpdateRow(selectedJob.jobId, sid);
      showSuccess('Staged row deleted successfully.');
      if (viewingRow?.stagingUpdateId === sid) setViewingRow(null);
      if (editingRow?.stagingUpdateId === sid) { setEditingRow(null); setEditFormData(EMPTY_EDIT); setStagedRowPracticeId(''); }
      if (promoteModalRow?.stagingUpdateId === sid) { setPromoteModalRow(null); setPromoteFormData(null); setStagedRowPracticeId(''); }
      setShowDeleteRowConfirm(false);
      setRowToDelete(null);
      const pageAfterDelete = rows.length === 1 && currentPage > 0 ? currentPage - 1 : currentPage;
      await fetchJobRows(selectedJob.jobId, pageAfterDelete, filterErrors);
    } catch (err) {
      showError(err?.data?.message || err?.message || 'Failed to delete row.');
    } finally {
      setDeletingRowId(null);
    }
  };

  const displayedRows = (() => {
    let list = rows;
    if (rowSearch.trim()) {
      const q = rowSearch.trim().toLowerCase();
      list = rows.filter(r => {
        const text = [
          r.sheetRowNumber, r.rowStatus, r.checkNumber, r.practiceCode, r.locationCode, r.checkType,
          r.payer, r.exchange, r.workflowStatus, r.bankTrnRef, r.assignee, r.reportee, r.legacyNotes,
          ...(r.validationErrors || []),
        ].filter(Boolean).join(' ').toLowerCase();
        return text.includes(q);
      });
    }
    if (!sortField) return list;
    return [...list].sort((a, b) => {
      const normalize = (val) => (val ?? '').toString().toLowerCase();
      const aVal = sortField === 'sheetRowNumber' ? Number(a[sortField] ?? 0) : normalize(a[sortField]);
      const bVal = sortField === 'sheetRowNumber' ? Number(b[sortField] ?? 0) : normalize(b[sortField]);
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const practiceOptions = [
    { value: '', label: 'Select Practice' },
    ...practices.map(p => ({ value: p.practiceId || p.id, label: `${p.name} (${p.code})`, code: p.code })),
  ];
  const locationOptions = [
    { value: '', label: 'Select Location' },
    ...locations.map(l => ({ value: l.code, label: `${l.name} (${l.code})` })),
  ];
  const stagedRowLocationOptions = [
    { value: '', label: 'Select Location' },
    ...stagedRowLocations.map((l) => ({ value: l.code, label: `${l.name} (${l.code})` })),
  ];
  const errorFilterOptions = [
    { value: '', label: 'All Errors' },
    ...(errorTypes || []).map(e => ({ value: e.code, label: e.name })),
  ];

  const isProcessing = uploading || revalidating || deleting || exporting || promotingAll;

  const personDisplay = useCallback((val) => {
    if (val == null || String(val).trim() === '') return null;
    const s = String(val).trim();
    const u = getUserById(s);
    if (u) return u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : (u.email || s);
    return s;
  }, [getUserById]);

  const handleDashboardPreviousPage = () => {
    if (dashboardPage > 0 && !loadingDashboard) {
      setDashboardPage((prev) => prev - 1);
    }
  };

  const handleDashboardNextPage = () => {
    if (dashboardHasMore && !loadingDashboard) {
      setDashboardPage((prev) => prev + 1);
    }
  };

  const handleDashboardPageInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setDashboardPageInputValue(value);
    }
  };

  const handleDashboardGoToPage = () => {
    if (dashboardPageInputValue === '' || loadingDashboard) return;
    const pageNum = parseInt(dashboardPageInputValue, 10);
    const maxPage = dashboardTotalPages > 0 ? dashboardTotalPages - 1 : 0;
    if (pageNum >= 1 && pageNum <= maxPage + 1) {
      setDashboardPage(pageNum - 1);
    } else {
      setDashboardPageInputValue((dashboardPage + 1).toString());
    }
  };

  const handleDashboardPageInputKeyPress = (e) => {
    if (e.key === 'Enter') handleDashboardGoToPage();
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'Practice Code': '',
        'Location Code': '',
        'Check Number': '',
        'Alt Check Number': '',
        'Check Type': '',
        'Payer': '',
        'Exchange': '',
        'Bank Trn Ref': '',
        'Batch Description': '',
        'Workflow Status': '',
        'legacyNotes': '',
        'assignee': '',
        'reportee': '',
      },
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws['!cols'] = [
      { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
      { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 24 }, { wch: 18 },
      { wch: 24 }, { wch: 14 }, { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Check Update Template');
    XLSX.writeFile(wb, 'CheckUpdate_Template.xlsx');
  };

  return (
    <div className="check-upload-page">
      {!selectedJob && (
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>Check Update</h1>
            <p className="page-subtitle" style={{ margin: 0 }}>Bulk update checks via staged rows and validation</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="btn-cancel"
              onClick={handleDownloadTemplate}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M3 17C3 18.1046 3.89543 19 5 19H15C16.1046 19 17 18.1046 17 17V13M13 10L10 13M10 13L7 10M10 13V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download Template
            </button>
            <button className="btn-upload-small" onClick={() => setShowUploadModal(true)} disabled={isProcessing}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginRight: '6px' }}>
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8M12 9V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Check Update
            </button>
            <Tooltip text="Download a sample XLSX column layout, then upload your file to bulk-update checks" position="bottom">
              <div className="info-icon-wrapper">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M10 14V10M10 6H10.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </Tooltip>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div className="modal-overlay" onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }}>
          <div className="upload-modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111827' }}>Upload Check Update File</h2>
              <button onClick={() => { if (!uploading) { setShowUploadModal(false); resetUploadModal(); } }} disabled={uploading}
                style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: uploading ? 'not-allowed' : 'pointer', padding: '0', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>×</button>
            </div>
            <div className="upload-section-modal">
              <div className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
                <input ref={fileInputRef} type="file" id="cu-file-input" accept=".csv,.xlsx" onChange={handleFileInput} style={{ display: 'none' }} />
                {!file ? (
                  <>
                    <div className="upload-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8M12 9V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="upload-text"><strong>Click to select CSV or XLSX file</strong><span>or drag and drop here</span></div>
                    <label htmlFor="cu-file-input" className="upload-label">Select File</label>
                  </>
                ) : (
                  <div className="file-selected">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="file-info"><strong>{file.name}</strong><span>{(file.size / 1024).toFixed(2)} KB</span></div>
                    <button className="btn-remove" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} disabled={isProcessing}>Remove</button>
                  </div>
                )}
              </div>
              {file && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>Job Name (Optional)</label>
                    <input type="text" value={jobName} onChange={e => setJobName(e.target.value)} placeholder="Enter job name"
                      style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Practice <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <SearchableDropdown
                      options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                      value={selectedPracticeId}
                      onChange={(val) => {
                        setSelectedPracticeId(val);
                        const found = practices.find(p => (p.practiceId || p.id) === val);
                        setPracticeCode(found?.code || ''); setLocationCode('');
                      }}
                      placeholder="Select Practice"
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Location <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <SearchableDropdown
                      options={loadingLocations ? [{ value: '', label: 'Loading...' }] : locationOptions}
                      value={locationCode}
                      onChange={setLocationCode}
                      placeholder={!selectedPracticeId ? 'Select practice first' : 'Select Location'}
                      disabled={!selectedPracticeId || loadingLocations}
                    />
                  </div>
                  <div className="upload-actions">
                    <button className="btn-cancel" onClick={resetUploadModal} disabled={isProcessing || uploading}>Cancel</button>
                    <button className="btn-upload" onClick={handleUpload} disabled={uploading || isProcessing || !file || !practiceCode || !locationCode}>
                      {uploading ? 'Uploading...' : 'Upload & Validate'}
                    </button>
                  </div>
                  {uploadProgress.active && (
                    <UploadProgressCard
                      title="Check update upload"
                      stageIndex={uploadProgress.stageIndex}
                      stageLabel={uploadProgress.stageLabel}
                      helperText={uploadProgress.helperText}
                      percent={uploadProgress.percent}
                      indeterminate={uploadProgress.indeterminate}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!selectedJob && (
        <>
          <div className="filters-section jobs-search-section">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                placeholder="Search by job name, file name, practice, or location..."
              />
            </div>
          </div>
          <div className="jobs-section" style={{ marginTop: '0' }}>
            <h2 className="section-title">Check Update Jobs</h2>
            {loadingDashboard ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading jobs...</div>
            ) : dashboardJobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No jobs found</div>
            ) : (
              <div className="jobs-table-wrapper">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <button className="btn-cancel" disabled={dashboardPage === 0 || loadingDashboard} onClick={handleDashboardPreviousPage} title="Previous page" aria-label="Previous page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    Showing {dashboardTotalElements === 0 ? 0 : dashboardPage * 50 + 1} - {Math.min((dashboardPage + 1) * 50, dashboardTotalElements)} of {dashboardTotalElements}
                  </div>
                  {dashboardTotalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Page:</span>
                      <input
                        type="text"
                        value={dashboardPageInputValue}
                        onChange={handleDashboardPageInputChange}
                        onKeyPress={handleDashboardPageInputKeyPress}
                        onBlur={handleDashboardGoToPage}
                        style={{ width: '46px', height: '26px', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center', fontSize: '12px' }}
                      />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>of {dashboardTotalPages}</span>
                    </div>
                  )}
                  <button className="btn-cancel" disabled={!dashboardHasMore || loadingDashboard} onClick={handleDashboardNextPage} title="Next page" aria-label="Next page" style={{ padding: '6px 10px', minWidth: '34px' }}>
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
                <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Job Name', 'File Name', 'Practice', 'Location', 'Status', 'Total', 'Valid', 'Invalid', 'Promoted', 'Created', 'Updated', 'Actions'].map(h => <th key={h} style={{ padding: '12px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {dashboardJobs.map(job => (
                      <tr key={job.jobId} style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }} onClick={() => fetchJobRows(job.jobId, 0, [])}>
                        <td style={{ padding: '12px' }}><TruncatedName text={job.jobName || 'Unnamed Job'} /></td>
                        <td style={{ padding: '12px' }}><TruncatedName text={job.uploadedFileName || '-'} /></td>
                        <td style={{ padding: '12px' }}>{job.practiceCode || '-'}</td>
                        <td style={{ padding: '12px' }}>{job.locationCode || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          <span className="job-status-badge" style={getJobStatusStyle(job.status)}>{formatJobStatus(job.status)}</span>
                        </td>
                        <td style={{ padding: '12px' }}>{job.totalRows || 0}</td>
                        <td style={{ padding: '12px', color: '#059669' }}>{job.validRows || 0}</td>
                        <td style={{ padding: '12px', color: '#dc2626' }}>{job.invalidRows || 0}</td>
                        <td style={{ padding: '12px', color: '#2563eb' }}>{job.promotedRows || 0}</td>
                        <td style={{ padding: '12px' }}><UserTimestamp userId={job.createdBy} dateTime={job.createdAt} action="created" getUserName={getUserName} getUserById={getUserById} /></td>
                        <td style={{ padding: '12px' }}><UserTimestamp userId={job.updatedBy} dateTime={job.updatedAt} action="updated" getUserName={getUserName} getUserById={getUserById} /></td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151' }}
                              onClick={(e) => { e.stopPropagation(); fetchJobRows(job.jobId, 0, []); }} title="View">
                              <MdOutlineVisibility size={16} />
                            </button>
                            <button style={{ padding: '6px 8px', color: '#dc2626', border: '1px solid #dc2626', background: 'white', display: 'flex', alignItems: 'center', borderRadius: '6px' }}
                              onClick={(e) => handleDeleteClick(job, e)} disabled={isProcessing || deleting} title="Delete job">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {selectedJob && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
              <button
                className="btn-cancel"
                onClick={() => { setSelectedJob(null); setRows([]); setFilterErrors([]); setRowSearch(''); }}
                disabled={isProcessing}
                style={{ flexShrink: 0 }}
              >
                ← Back
              </button>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: '0', fontSize: '20px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedJob.jobName || 'Unnamed Job'}
                </h2>
                <p style={{ margin: '2px 0 0 0', color: '#6b7280', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedJob.uploadedFileName} &bull; Practice: <strong>{selectedJob.practiceCode}</strong> &bull; Location: <strong>{selectedJob.locationCode}</strong> &bull; Created {formatDateTime(selectedJob.createdAt)}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                className="btn-cancel"
                onClick={handleExportReport}
                disabled={exporting || isProcessing || revalidating}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px' }}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M3 17C3 18.1046 3.89543 19 5 19H15C16.1046 19 17 18.1046 17 17V13M13 10L10 13M10 13L7 10M10 13V1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {exporting ? 'Exporting...' : 'Error Report'}
              </button>
              <button className="btn-primary" onClick={handleRevalidate} disabled={revalidating || isProcessing}>
                {revalidating ? 'Re-validating...' : 'Re-validate'}
              </button>
              <button className="btn-upload" onClick={handlePromoteAll} disabled={isProcessing || (selectedJob.validRows || 0) === 0}>
                {promotingAll ? 'Promoting...' : `Promote All (${selectedJob.validRows || 0})`}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Total</div><div style={{ fontSize: '24px' }}>{selectedJob.totalRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Valid</div><div style={{ fontSize: '24px', color: '#059669' }}>{selectedJob.validRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Invalid</div><div style={{ fontSize: '24px', color: '#dc2626' }}>{selectedJob.invalidRows || 0}</div></div>
            <div style={{ padding: '16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}><div style={{ fontSize: '12px', color: '#6b7280' }}>Promoted</div><div style={{ fontSize: '24px', color: '#2563eb' }}>{selectedJob.promotedRows || 0}</div></div>
          </div>

          <div className="filters-section bulk-job-detail-filters">
            <div className="search-bar">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={rowSearch}
                onChange={(e) => setRowSearch(e.target.value)}
                placeholder="Search rows..."
              />
            </div>
            <div className="filter-dropdowns filter-dropdowns--error-only">
              <SearchableDropdown
                options={errorTypesLoading ? [{ value: '', label: 'Loading...' }] : errorFilterOptions}
                value={filterErrors[0] || ''}
                onChange={handleFilterChange}
                placeholder="All Errors"
              />
              {filterErrors.length > 0 && (
                <button
                  type="button"
                  className="btn-cancel batch-validation-clear-errors"
                  onClick={() => { setFilterErrors([]); if (selectedJob) fetchJobRows(selectedJob.jobId, 0, []); }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>Showing {currentPage * 50 + 1} - {Math.min((currentPage + 1) * 50, totalElements)} of {totalElements}</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-cancel" disabled={currentPage === 0 || loadingRows} onClick={() => fetchJobRows(selectedJob.jobId, currentPage - 1, filterErrors)}>Previous</button>
              <button className="btn-cancel" disabled={currentPage >= totalPages - 1 || loadingRows} onClick={() => fetchJobRows(selectedJob.jobId, currentPage + 1, filterErrors)}>Next</button>
            </div>
          </div>

          <div className="checks-table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="checks-table" style={{ minWidth: '1450px' }}>
              <thead>
                <tr>
                  {[
                    { label: 'Row', field: 'sheetRowNumber' },
                    { label: 'Status', field: 'rowStatus' },
                    { label: 'Check', field: 'checkNumber' },
                    { label: 'Practice', field: 'practiceCode' },
                    { label: 'Location', field: 'locationCode' },
                    { label: 'Check Type', field: 'checkType' },
                    { label: 'Payer', field: 'payer' },
                    { label: 'Exchange', field: 'exchange' },
                    { label: 'Workflow Status', field: 'workflowStatus' },
                    { label: 'Bank TRN Ref', field: 'bankTrnRef' },
                    { label: 'Errors', field: null },
                    { label: 'Actions', field: null },
                  ].map(({ label, field }) => (
                    <th key={label}
                      onClick={field ? () => handleSort(field) : undefined}
                      style={{ padding: '12px', textAlign: 'left', cursor: field ? 'pointer' : 'default', userSelect: 'none' }}>
                      {label}{field && <SortArrow field={field} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loadingRows ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: '40px' }}>Loading rows...</td></tr>
                ) : displayedRows.length === 0 ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No rows found</td></tr>
                ) : displayedRows.map(row => {
                  const isPromotingThisRow = promotingRowId === row.stagingUpdateId;
                  const isDeletingThisRow = deletingRowId === row.stagingUpdateId;
                  const rowIsPromoted = String(row.rowStatus || '').toUpperCase() === 'PROMOTED';
                  return (
                    <tr key={row.stagingUpdateId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px' }}>{formatRowNumber(row.sheetRowNumber)}</td>
                      <td style={{ padding: '12px' }}>
                        <span className="job-status-badge" style={getRowStatusStyle(row.rowStatus)}>{row.rowStatus || 'UNKNOWN'}</span>
                      </td>
                      <td style={{ padding: '12px' }}><TruncatedText text={row.checkNumber} maxLength={18} /></td>
                      <td style={{ padding: '12px' }}>{row.practiceCode || '-'}</td>
                      <td style={{ padding: '12px' }}>{row.locationCode || '-'}</td>
                      <td style={{ padding: '12px' }}>{row.checkType || '-'}</td>
                      <td style={{ padding: '12px' }}><TruncatedText text={row.payer} maxLength={20} /></td>
                      <td style={{ padding: '12px' }}><TruncatedText text={row.exchange} maxLength={20} /></td>
                      <td style={{ padding: '12px' }}><TruncatedText text={row.workflowStatus} maxLength={18} /></td>
                      <td style={{ padding: '12px' }}><TruncatedText text={row.bankTrnRef} maxLength={18} /></td>
                      <td style={{ padding: '12px', maxWidth: '220px' }}>
                        <RowValidationErrorsCell validationErrors={row.validationErrors} />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {/* View */}
                          <button title="View details" style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', cursor: 'pointer' }} onClick={() => setViewingRow(row)}><MdOutlineVisibility size={15} /></button>
                          {!rowIsPromoted && (
                            <button title="Edit row" style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#2563eb', cursor: isProcessing ? 'not-allowed' : 'pointer' }} onClick={() => handleEditRow(row)} disabled={isProcessing}><MdOutlineEdit size={15} /></button>
                          )}
                          {!rowIsPromoted && (
                            <button
                              title={isDeletingThisRow ? 'Deleting...' : 'Delete row'}
                              style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', cursor: (isDeletingThisRow || deleting || !!deletingRowId) ? 'not-allowed' : 'pointer' }}
                              onClick={(e) => handleDeleteRowClick(row, e)}
                              disabled={isProcessing || deleting || !!deletingRowId}
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                          {!rowIsPromoted && (
                            <button
                              title={isPromotingThisRow ? 'Promoting...' : 'Promote row'}
                              style={{
                                padding: '5px 7px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'white',
                                border: '1px solid #bfdbfe',
                                borderRadius: '6px',
                                color: '#2563eb',
                                cursor: (isPromotingThisRow || isProcessing || promoteModalRow) ? 'not-allowed' : 'pointer',
                                opacity: isPromotingThisRow ? 0.7 : 1,
                              }}
                              onClick={() => openPromoteModal(row)}
                              disabled={isPromotingThisRow || isProcessing || promoteModalRow}
                            >
                              <ArrowUpCircle size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewingRow && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={() => setViewingRow(null)}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '960px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Check Update Row Details</h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Row #{formatRowNumber(viewingRow.sheetRowNumber)} &bull; {viewingRow.checkNumber || '-'}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="job-status-badge job-status-badge--emphasis" style={getRowStatusStyle(viewingRow.rowStatus)}>
                  {viewingRow.rowStatus || 'UNKNOWN'}
                </span>
                <button onClick={() => setViewingRow(null)} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            </div>
            {viewingRow.validationErrors?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <ErrorsBlock validationErrors={viewingRow.validationErrors} />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 24px' }}>
              <DetailRow label="Check Number" value={viewingRow.checkNumber} />
              <DetailRow
                label="Alt Check Number"
                value={firstRowString(viewingRow, ['altCheckNumber', 'alt_check_number', 'altChecknumber'])}
              />
              <DetailRow label="Practice Code" value={viewingRow.practiceCode} />
              <DetailRow label="Location Code" value={viewingRow.locationCode} />
              <DetailRow label="Check Type" value={viewingRow.checkType} />
              <DetailRow label="Payer" value={viewingRow.payer} />
              <DetailRow label="Exchange" value={viewingRow.exchange} />
              <div style={{ gridColumn: '1 / -1', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Batch Description</div>
                <div style={{ fontSize: '14px', color: '#111827', wordBreak: 'break-word' }}>
                  {firstRowString(viewingRow, ['batchDescription', 'batch_description', 'batchDesc']) || <span style={{ color: '#9ca3af' }}>—</span>}
                </div>
                {!firstRowString(viewingRow, ['batchDescription', 'batch_description', 'batchDesc']) && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                    This value is not returned by the API yet; it will appear here when the backend includes it on staged rows.
                  </div>
                )}
              </div>
              <DetailRow label="Workflow Status" value={viewingRow.workflowStatus} />
              <DetailRow label="Bank TRN Ref" value={viewingRow.bankTrnRef} />
              <DetailRow label="Assignee" value={personDisplay(viewingRow.assigneeId || viewingRow.assignee)} />
              <DetailRow label="Reportee" value={personDisplay(viewingRow.reporterId || viewingRow.reportee)} />
              <DetailRow label="Last Validated" value={viewingRow.lastValidatedAt ? new Date(viewingRow.lastValidatedAt).toLocaleString('en-US') : null} />
              <DetailRow label="Created At" value={viewingRow.createdAt ? new Date(viewingRow.createdAt).toLocaleString('en-US') : null} />
              <DetailRow label="Updated At" value={viewingRow.updatedAt ? new Date(viewingRow.updatedAt).toLocaleString('en-US') : null} />
              <DetailRow label="Legacy Notes" value={viewingRow.legacyNotes} wide />
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setViewingRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editingRow && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '980px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Edit Check Update Row</h3>
              <button onClick={() => { setEditingRow(null); setEditFormData(EMPTY_EDIT); setStagedRowPracticeId(''); }} disabled={savingEdit}
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#6b7280' }}>Row #{formatRowNumber(editingRow.sheetRowNumber)} &bull; {editingRow.checkNumber || '-'} — Saves and auto re-validates</p>
            {editingRow.validationErrors?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <ErrorsBlock validationErrors={editingRow.validationErrors} />
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 20px' }}>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Check Number</label><input type="text" value={editFormData.checkNumber} onChange={e => setEditFormData(p => ({ ...p, checkNumber: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Practice <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                  value={stagedRowPracticeId}
                  onChange={(val) => {
                    setStagedRowPracticeId(val);
                    const found = practices.find((p) => (p.practiceId || p.id) === val);
                    setEditFormData((p) => ({ ...p, practiceCode: found?.code || '', locationCode: '' }));
                  }}
                  placeholder="Select Practice"
                  compact={true}
                  disabled={loadingPractices}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Location <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={loadingStagedRowLocations ? [{ value: '', label: 'Loading...' }] : stagedRowLocationOptions}
                  value={editFormData.locationCode}
                  onChange={(val) => setEditFormData((p) => ({ ...p, locationCode: val }))}
                  placeholder={!stagedRowPracticeId ? 'Select practice first' : 'Select Location'}
                  compact={true}
                  disabled={!stagedRowPracticeId || loadingStagedRowLocations}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Check Type</label>
                <SearchableDropdown
                  options={[{ value: '', label: 'Select Check Type' }, ...(checkTypes || []).map(ct => ({ value: ct.code, label: ct.name }))]}
                  value={editFormData.checkType}
                  onChange={val => setEditFormData(p => ({ ...p, checkType: val }))}
                  placeholder="Select Check Type"
                  compact={true}
                  disabled={checkTypesLoading}
                />
              </div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Payer</label><input type="text" value={editFormData.payer} onChange={e => setEditFormData(p => ({ ...p, payer: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Exchange</label><input type="text" value={editFormData.exchange} onChange={e => setEditFormData(p => ({ ...p, exchange: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Workflow Status</label>
                <SearchableDropdown
                  options={[{ value: '', label: 'Select Workflow Status' }, ...(workflowStatuses || []).map(ws => ({ value: ws.code, label: ws.name }))]}
                  value={editFormData.workflowStatus}
                  onChange={val => setEditFormData(p => ({ ...p, workflowStatus: val }))}
                  placeholder="Select Workflow Status"
                  compact={true}
                  disabled={workflowStatusesLoading}
                />
              </div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Bank TRN Ref</label><input type="text" value={editFormData.bankTrnRef} onChange={e => setEditFormData(p => ({ ...p, bankTrnRef: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Assignee <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={assigneePickerOptions}
                  value={editFormData.assignee}
                  onChange={(val) => setEditFormData((p) => ({ ...p, assignee: val }))}
                  placeholder="Select assignee"
                  compact={true}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Reportee <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={reporteePickerOptions}
                  value={editFormData.reportee}
                  onChange={(val) => setEditFormData((p) => ({ ...p, reportee: val }))}
                  placeholder="Select reportee"
                  compact={true}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Legacy Notes</label><textarea value={editFormData.legacyNotes} onChange={e => setEditFormData(p => ({ ...p, legacyNotes: e.target.value }))} rows={3} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }} /></div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => { setEditingRow(null); setEditFormData(EMPTY_EDIT); setStagedRowPracticeId(''); }} disabled={savingEdit}>Cancel</button>
              <button className="btn-upload" onClick={handleSaveEdit} disabled={savingEdit}>{savingEdit ? 'Saving...' : 'Save & Re-validate'}</button>
            </div>
          </div>
        </div>
      )}

      {promoteModalRow && promoteFormData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
          onClick={closePromoteModal}>
          <div style={{ background: 'white', borderRadius: '10px', padding: '24px', maxWidth: '980px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>Promote row</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
                  Row #{formatRowNumber(promoteModalRow.sheetRowNumber)} — Edit staged check fields, then promote
                </p>
              </div>
              <button type="button" onClick={closePromoteModal} disabled={promotingFromModal}
                style={{ background: 'none', border: 'none', fontSize: '22px', color: '#6b7280', cursor: promotingFromModal ? 'not-allowed' : 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {promoteModalRow.validationErrors?.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <ErrorsBlock validationErrors={promoteModalRow.validationErrors} />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 20px' }}>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Check Number</label><input type="text" value={promoteFormData.checkNumber} onChange={e => setPromoteFormData(p => ({ ...p, checkNumber: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Practice <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={loadingPractices ? [{ value: '', label: 'Loading...' }] : practiceOptions}
                  value={stagedRowPracticeId}
                  onChange={(val) => {
                    setStagedRowPracticeId(val);
                    const found = practices.find((p) => (p.practiceId || p.id) === val);
                    setPromoteFormData((p) => (p ? { ...p, practiceCode: found?.code || '', locationCode: '' } : p));
                  }}
                  placeholder="Select Practice"
                  compact={true}
                  disabled={loadingPractices || promotingFromModal}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Location <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={loadingStagedRowLocations ? [{ value: '', label: 'Loading...' }] : stagedRowLocationOptions}
                  value={promoteFormData.locationCode}
                  onChange={(val) => setPromoteFormData((p) => (p ? { ...p, locationCode: val } : p))}
                  placeholder={!stagedRowPracticeId ? 'Select practice first' : 'Select Location'}
                  compact={true}
                  disabled={!stagedRowPracticeId || loadingStagedRowLocations || promotingFromModal}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Check Type</label>
                <SearchableDropdown
                  options={[{ value: '', label: 'Select Check Type' }, ...(checkTypes || []).map(ct => ({ value: ct.code, label: ct.name }))]}
                  value={promoteFormData.checkType}
                  onChange={val => setPromoteFormData(p => ({ ...p, checkType: val }))}
                  placeholder="Select Check Type"
                  compact={true}
                  disabled={checkTypesLoading}
                />
              </div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Payer</label><input type="text" value={promoteFormData.payer} onChange={e => setPromoteFormData(p => ({ ...p, payer: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Exchange</label><input type="text" value={promoteFormData.exchange} onChange={e => setPromoteFormData(p => ({ ...p, exchange: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Workflow Status</label>
                <SearchableDropdown
                  options={[{ value: '', label: 'Select Workflow Status' }, ...(workflowStatuses || []).map(ws => ({ value: ws.code, label: ws.name }))]}
                  value={promoteFormData.workflowStatus}
                  onChange={val => setPromoteFormData(p => ({ ...p, workflowStatus: val }))}
                  placeholder="Select Workflow Status"
                  compact={true}
                  disabled={workflowStatusesLoading}
                />
              </div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Bank TRN Ref</label><input type="text" value={promoteFormData.bankTrnRef} onChange={e => setPromoteFormData(p => ({ ...p, bankTrnRef: e.target.value }))} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} /></div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Assignee <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={assigneePickerOptions}
                  value={promoteFormData.assignee}
                  onChange={(val) => setPromoteFormData((p) => (p ? { ...p, assignee: val } : p))}
                  placeholder="Select assignee"
                  compact={true}
                  disabled={promotingFromModal}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Reportee <span style={{ color: '#dc2626' }}>*</span></label>
                <SearchableDropdown
                  options={reporteePickerOptions}
                  value={promoteFormData.reportee}
                  onChange={(val) => setPromoteFormData((p) => (p ? { ...p, reportee: val } : p))}
                  placeholder="Select reportee"
                  compact={true}
                  disabled={promotingFromModal}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>Legacy Notes</label><textarea value={promoteFormData.legacyNotes} onChange={e => setPromoteFormData(p => ({ ...p, legacyNotes: e.target.value }))} rows={3} style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }} /></div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
              <button type="button" className="btn-cancel" onClick={closePromoteModal} disabled={promotingFromModal}>Cancel</button>
              <button type="button" className="btn-upload" onClick={handleConfirmPromoteFromModal} disabled={promotingFromModal}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <ArrowUpCircle size={18} />
                {promotingFromModal ? 'Promoting...' : 'Promote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="upload-modal-content" style={{ maxWidth: '420px' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Confirm Delete Job</h3>
            <p>Delete job "<strong>{jobToDelete?.jobName || 'Unnamed Job'}</strong>"? This removes all staged rows permanently.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => { setShowDeleteConfirm(false); setJobToDelete(null); }}>Cancel</button>
              <button style={{ padding: '8px 20px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white' }} onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteRowConfirm && rowToDelete && (
        <div className="modal-overlay">
          <div className="upload-modal-content" style={{ maxWidth: '420px' }}>
            <h3 style={{ marginTop: 0, color: '#dc2626' }}>Confirm Delete Row</h3>
            <p>
              Delete staged row <strong>#{formatRowNumber(rowToDelete.sheetRowNumber)}</strong>
              {rowToDelete.checkNumber ? <> ({rowToDelete.checkNumber})</> : null}? This cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-cancel" onClick={() => { if (!deletingRowId) { setShowDeleteRowConfirm(false); setRowToDelete(null); } }} disabled={!!deletingRowId}>Cancel</button>
              <button style={{ padding: '8px 20px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white' }} onClick={handleDeleteRowConfirm} disabled={!!deletingRowId}>
                {deletingRowId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckUpdate;
